import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { faPen as faPenSolid } from '@fortawesome/pro-solid-svg-icons';
import {
    ICorporationResponse,
    ILocationResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { DataCellRenderInfo, TableColumn } from 'components/data_table/types';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { NotificationPropertiesCell } from 'components/notification_subscriptions/NotificationProperties';
import { NotificationSubscriptionHierarchy } from 'components/notification_subscriptions/NotificationSubscriptionHierarchy';
import {
    NotificationSubscription,
    NotificationSubscriptionsPagedDataSource,
} from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { EditHardwareHealthNotificationSubscriptionModal } from 'pages/Settings/NotificationSubscriptions/EditHardwareHealthNotificationSubscriptionModal';
import { EditHardwareNotificationSubscriptionModal } from 'pages/Settings/NotificationSubscriptions/EditHardwareNotificationSubscriptionModal';
import { EditIndicatorAlertsNotificationSubscriptionModal } from 'pages/Settings/NotificationSubscriptions/EditIndicatorAlertsNotificationSubscriptionModal';
import { EditMachineHealthNotificationSubscriptionModal } from 'pages/Settings/NotificationSubscriptions/EditMachineHealthNotificationSubscriptionModal';
import { NotificationSubscriptionDeleteButton } from 'pages/Settings/NotificationSubscriptions/IndicatorAlerts/NotificationSubscriptionDeleteButton';
import {
    timespanAsUnacknowledgedPeriodString,
    UnacknowledgedPeriodInfoPopup,
} from 'pages/Settings/NotificationSubscriptions/IndicatorAlerts/shared';
import { NotificationSubscriptionToggleButton } from 'pages/Settings/NotificationSubscriptions/NotificationSubscriptionToggleButton';
import {
    getNotificationTypeDisplayName,
    isIndicatorAlertsSubscription,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { useMemo, useState } from 'react';
import { tokens } from 'styles/tokens';
import { getId } from 'utils/data';
import { TimeSpan } from 'utils/TimeSpan';

export type UserNotificationSubscriptionsTableProps = Readonly<{
    location?: ILocationResponse;
    corporation?: ICorporationResponse;
    dataSource: NotificationSubscriptionsPagedDataSource;
    isDetailView?: boolean;
}>;

export function UserNotificationSubscriptionsTable({
    corporation,
    dataSource,
    isDetailView = false,
}: UserNotificationSubscriptionsTableProps) {
    const { reloadPage } = dataSource;

    const [
        selectedSubscription,
        setSelectedSubscription,
    ] = useState<NotificationSubscription>();

    const onClose = (saved: boolean) => {
        setSelectedSubscription(undefined);
        if (saved) {
            reloadPage();
        }
    };

    const columns = useMemo<TableColumn<NotificationSubscription>[]>(
        () => [
            {
                header: () => 'Notification Type',
                id: 'Type',
                value: ({ rowData }) => <>{getNotificationTypeDisplayName(rowData)}</>,
            },
            {
                header: () => 'Path',
                id: 'Path',
                value: ({ rowData }) => (
                    <div className="text-nowrap">
                        <NotificationSubscriptionHierarchy
                            subscription={rowData}
                            showLocation
                            showCorporation={corporation === undefined}
                        />
                    </div>
                ),
                primary: true,
                locked: true,
                sortOn: ['CorporationName', 'LocationName', 'GroupName'],
            },
            {
                header: () => 'Notification Properties',
                id: 'NotificationProperties',
                value: NotificationPropertiesCell,
            },
            {
                header: () => 'Subscriber',
                id: 'Subscriber',
                enabled: isDetailView,
                value: ({ rowData }: DataCellRenderInfo<NotificationSubscription>) => (
                    <div className="text-nowrap">
                        {rowData.User.UserFirstName} {rowData.User.UserLastName}
                    </div>
                ),
            },
            {
                header: () => (
                    <div className="flex align-center">
                        Unacknowledged Period
                        <UnacknowledgedPeriodInfoPopup />
                    </div>
                ),
                id: 'UnacknowledgedPeriod',
                value: UnacknowledgedPeriodCell,
            },
            {
                header: () => 'Actions',
                id: 'Actions',
                locked: true,
                value: ({ rowData }) => (
                    <ActionMenuTableCell>
                        <NotificationSubscriptionToggleButton
                            subscription={rowData}
                            onToggle={reloadPage}
                        />
                        <HoverIconButton
                            title={`Edit ${getNotificationTypeDisplayName(
                                rowData
                            )} notification subscription`}
                            icon={faPen}
                            hoverIcon={faPenSolid}
                            color={tokens.icon.actionActive}
                            hoverColor={tokens.icon.actionHover}
                            size="14"
                            onClick={() => setSelectedSubscription(rowData)}
                        />
                        <NotificationSubscriptionDeleteButton
                            subscription={rowData}
                            onDelete={reloadPage}
                        />
                    </ActionMenuTableCell>
                ),
            },
        ],
        [isDetailView, corporation, reloadPage]
    );

    return (
        <>
            <DataSourceTable
                dataSource={dataSource}
                rowKey={getId}
                columns={columns}
                noResultMessage="No notification subscriptions found."
            />
            {selectedSubscription && (
                <NotificationSubscriptionEditModal
                    subscription={selectedSubscription}
                    onClose={onClose}
                />
            )}
        </>
    );
}

export const NotificationSubscriptionEditModal = ({
    subscription,
    onClose,
}: {
    subscription: NotificationSubscription;
    onClose: (saved: boolean) => void;
}) => (
    <>
        {subscription.discriminator === 'Indicator' && (
            <EditIndicatorAlertsNotificationSubscriptionModal
                notificationSubscription={subscription}
                onClose={onClose}
            />
        )}
        {subscription.discriminator === 'Hardware' && (
            <EditHardwareNotificationSubscriptionModal
                notificationSubscription={subscription}
                onClose={onClose}
            />
        )}
        {subscription.discriminator === 'MachineHealth' && (
            <EditMachineHealthNotificationSubscriptionModal
                notificationSubscription={subscription}
                onClose={onClose}
            />
        )}
        {subscription.discriminator === 'HardwareHealth' && (
            <EditHardwareHealthNotificationSubscriptionModal
                notificationSubscription={subscription}
                onClose={onClose}
            />
        )}
    </>
);

const UnacknowledgedPeriodCell = ({
    rowData,
}: DataCellRenderInfo<NotificationSubscription>) => {
    const renderedTime = useMemo(() => {
        if (isIndicatorAlertsSubscription(rowData)) {
            const period = TimeSpan.parse(rowData.UnacknowledgedPeriod);
            if (period.asMilliseconds() === 0) {
                return '0 (Instant)';
            }
            return timespanAsUnacknowledgedPeriodString(period);
        }
        return 'N/A';
    }, [rowData]);
    return <div className="text-nowrap">{renderedTime}</div>;
};
